import { create } from "zustand";
import { persist } from "zustand/middleware";
import { LIMITS } from "@shared/commission";
import { STEP_IDS, type BlockKey, type CharacterDraft, type IntakeDraft, type StepId, type PostingPermission } from "../types";
import type { BackgroundType, UsageType, ContentRating } from "../types";

export interface IntakeActions {
  startForArtist(slug: string): void;
  goTo(step: StepId): void; next(): void; back(): void;
  setCharacterCount(n: number, max?: number): void;
  /** "Do they all have the same body type?" Turning it back on copies the first character's type to all. */
  setSameType(same: boolean): void;
  setAllTypes(type: string): void;
  setCharacterType(id: string, type: string): void;
  updateCharacter(id: string, patch: { name?: string }): void;
  setBlockText(id: string, block: BlockKey, text: string): void;
  addBlockRef(id: string, block: BlockKey, url: string): void;
  removeBlockRef(id: string, block: BlockKey, url: string): void;
  setBackgroundType(b: BackgroundType): void; setSceneDescription(s: string): void;
  addSceneRef(url: string): void; removeSceneRef(url: string): void;
  setUsageType(u: UsageType): void; setContentRating(r: ContentRating): void;
  setDeadlineMode(m: "none" | "date"): void; setDeadline(d: string | null): void;
  toggleManualFee(key: string): void; setNotes(s: string): void;
  setPostingPermission(p: PostingPermission): void;
  setContact(patch: Partial<Pick<IntakeDraft, "clientName" | "clientEmail" | "clientDiscord">>): void;
  setTosAccepted(v: boolean): void; dismissNudge(id: string): void;
  resetAfterSubmission(): void;
}

const newCharacter = (type: string | null = null): CharacterDraft => ({
  id: globalThis.crypto.randomUUID(), name: "", type,
  look: { text: "", refs: [] }, action: { text: "", refs: [] }, extra: { text: "", refs: [] },
});

/** The body type of each character, in order (null = not chosen yet). */
export const draftTypes = (d: Pick<IntakeDraft, "characters" | "characterCount">) =>
  d.characters.slice(0, d.characterCount).map((c) => c.type);

function commissionDefaults(): Omit<IntakeDraft, "artistSlug" | "clientName" | "clientEmail" | "clientDiscord" | "dismissedNudges"> {
  return {
    step: "type", direction: 1,
    sameType: true, characterCount: 1, characters: [newCharacter()],
    backgroundType: "none", sceneDescription: "", sceneRefs: [],
    usageType: null, contentRating: null, deadlineMode: "none", deadline: null,
    manualFees: [], notes: "", postingPermission: null, tosAccepted: false,
  };
}

const initialDraft = (): IntakeDraft => ({
  artistSlug: null, clientName: "", clientEmail: "", clientDiscord: "", dismissedNudges: [], ...commissionDefaults(),
});

const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const addUnique = (list: string[], v: string) => (list.includes(v) ? list : [...list, v]);

export function migrateDraft(persisted: unknown, version: number): IntakeDraft {
  const old = (persisted ?? {}) as Partial<IntakeDraft>;
  const base = initialDraft();
  if (version < 7) {
    return { ...base, clientName: old.clientName ?? "", clientEmail: old.clientEmail ?? "", clientDiscord: old.clientDiscord ?? "" };
  }
  return { ...base, ...old };
}

export const useCommissionStore = create<IntakeDraft & IntakeActions>()(
  persist(
    (set, get) => {
      const mapChar = (id: string, fn: (c: CharacterDraft) => CharacterDraft) =>
        set((s) => ({ characters: s.characters.map((c) => (c.id === id ? fn(c) : c)) }));
      return {
        ...initialDraft(),

        startForArtist: (slug) => {
          if (get().artistSlug === slug) return;
          set({ artistSlug: slug, ...commissionDefaults() });
        },

        goTo: (step) => set((s) => ({ step, direction: STEP_IDS.indexOf(step) >= STEP_IDS.indexOf(s.step) ? 1 : -1 })),
        next: () => set((s) => ({ step: STEP_IDS[Math.min(STEP_IDS.indexOf(s.step) + 1, STEP_IDS.length - 1)], direction: 1 })),
        back: () => set((s) => ({ step: STEP_IDS[Math.max(STEP_IDS.indexOf(s.step) - 1, 0)], direction: -1 })),

        setCharacterCount: (n, max = LIMITS.maxCharacters) => set((s) => {
          const count = Math.max(1, Math.min(max, Math.round(n)));
          const characters = s.characters.slice(0, count);
          const inherited = s.sameType ? s.characters[0]?.type ?? null : null;
          while (characters.length < count) characters.push(newCharacter(inherited));
          return { characterCount: count, characters };
        }),
        setSameType: (sameType) => set((s) => ({
          sameType,
          characters: sameType ? s.characters.map((c) => ({ ...c, type: s.characters[0]?.type ?? null })) : s.characters,
        })),
        setAllTypes: (type) => set((s) => ({ characters: s.characters.map((c) => ({ ...c, type })) })),
        setCharacterType: (id, type) => mapChar(id, (c) => ({ ...c, type })),
        updateCharacter: (id, patch) => mapChar(id, (c) => ({ ...c, ...patch })),
        setBlockText: (id, block, text) => mapChar(id, (c) => ({ ...c, [block]: { ...c[block], text } })),
        addBlockRef: (id, block, url) => mapChar(id, (c) => ({ ...c, [block]: { ...c[block], refs: addUnique(c[block].refs, url) } })),
        removeBlockRef: (id, block, url) => mapChar(id, (c) => ({ ...c, [block]: { ...c[block], refs: c[block].refs.filter((u) => u !== url) } })),

        setBackgroundType: (backgroundType) => set(
          backgroundType === "complex" ? { backgroundType } : { backgroundType, sceneDescription: "", sceneRefs: [] },
        ),
        setSceneDescription: (sceneDescription) => set({ sceneDescription }),
        addSceneRef: (url) => set((s) => ({ sceneRefs: addUnique(s.sceneRefs, url) })),
        removeSceneRef: (url) => set((s) => ({ sceneRefs: s.sceneRefs.filter((u) => u !== url) })),

        setUsageType: (usageType) => set({ usageType }),
        setContentRating: (contentRating) => set({ contentRating }),
        setDeadlineMode: (deadlineMode) => set(deadlineMode === "none" ? { deadlineMode, deadline: null } : { deadlineMode }),
        setDeadline: (deadline) => set({ deadline }),
        toggleManualFee: (key) => set((s) => ({ manualFees: toggle(s.manualFees, key) })),
        setNotes: (notes) => set({ notes }),
        setPostingPermission: (postingPermission) => set({ postingPermission }),
        setContact: (patch) => set(patch),
        setTosAccepted: (tosAccepted) => set({ tosAccepted }),
        dismissNudge: (id) => set((s) => ({ dismissedNudges: addUnique(s.dismissedNudges, id) })),

        resetAfterSubmission: () => set((s) => ({ ...commissionDefaults(), artistSlug: s.artistSlug, step: "review" })),
      };
    },
    {
      name: "studiocomm-commission-form",
      version: 7,
      migrate: migrateDraft,
      partialize: (s) => {
        const { dismissedNudges: _d, ...rest } = s;
        return Object.fromEntries(Object.entries(rest).filter(([, v]) => typeof v !== "function")) as Partial<IntakeDraft>;
      },
    },
  ),
);
